import { FlexColumn, LabeledInput } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useRecoilState, useRecoilValue } from 'recoil';
import { REQUIRED_FIELD_TEXT } from '../../../constants/app';
import { useForms } from '../../../hooks/useForms';
import { useStyles } from '../../../hooks/useStyles';
import { editJobTypeState, editJobTypeTouchedState, editJobTypeValidState } from '../../../storage/jobTypesStates';
import { JobType, JobTypeRequest, JobTypeRequestTouched } from '../../../types/jobs';
import { TenantSelect } from '../../tenants/TenantSelect';
import JobTypeCategorySelect from './JobTypeCategorySelect';

type Props = {
  jobType?: JobType;
};

export function JobTypeFormFields({ jobType }: Readonly<Props>) {
  const editMode = jobType !== undefined;
  const { css, theme } = useStyles();
  const [form, setForm] = useRecoilState(editJobTypeState);
  const [formTouched, setFormTouched] = useRecoilState(editJobTypeTouchedState);
  const formValid = useRecoilValue(editJobTypeValidState);
  const { onTouched } = useForms<JobTypeRequest, JobTypeRequestTouched>(setForm, setFormTouched);

  const onChangeInput = (key: keyof JobTypeRequest, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <FlexColumn
      gap={theme.sizing.scale500}
      classNames={css({
        overflow: 'auto',
        height: '100%',
      })}
    >
      <JobTypeCategorySelect
        label="Categoría"
        selectedId={form.category}
        onChangeId={(value) => {
          onChangeInput('category', value);
        }}
        onBlur={() => onTouched('categoryTouched')}
        error={formTouched.categoryTouched && !formValid.category && REQUIRED_FIELD_TEXT}
        required
      />
      <LabeledInput
        label="Nombre"
        placeholder="Ingresa el nombre"
        value={form.name}
        onChange={(value) => {
          onChangeInput('name', value);
        }}
        onBlur={() => onTouched('nameTouched')}
        error={formTouched.nameTouched && !formValid.name && REQUIRED_FIELD_TEXT}
        required
      />
      <LabeledInput
        label="Descripción"
        placeholder="Ingresa la descripción"
        value={form.description}
        onChange={(value) => {
          onChangeInput('description', value);
        }}
      />
      <LabeledInput
        label="Duración promedio"
        placeholder="Ingresa la duración promedio en minutos"
        value={form.avgDuration ? form.avgDuration / 60 : undefined}
        endEnhancer="min."
        type="number"
        onChange={(value) => {
          onChangeInput('avgDuration', +value * 60);
        }}
        onBlur={() => onTouched('avgDurationTouched')}
        error={formTouched.avgDurationTouched && !formValid.avgDuration && REQUIRED_FIELD_TEXT}
        required
      />
      <LabeledInput
        label="Costo"
        placeholder="Ingresa el costo"
        value={form.cost}
        type="number"
        startEnhancer="$"
        onChange={(value) => {
          onChangeInput('cost', +value);
        }}
        onBlur={() => onTouched('costTouched')}
        error={formTouched.costTouched && !formValid.cost && REQUIRED_FIELD_TEXT}
        required
      />
      <TenantSelect
        label="Clientes"
        selectedTenantIds={form.tenants?.length !== 0 ? form.tenants : undefined}
        tenants={jobType?.tenants}
        onChangeTenantIds={(value) => {
          onChangeInput('tenants', value);
        }}
        onBlur={() => onTouched('tenantsTouched')}
        error={formTouched.tenantsTouched && !formValid.tenants && REQUIRED_FIELD_TEXT}
        isMulti
        required
      />
      {editMode && (
        <Checkbox
          checked={form.active}
          onChange={(e) => onChangeInput('active', e.target.checked)}
        >
          Activo
        </Checkbox>
      )}
    </FlexColumn>
  );
}
